import React, { useCallback, useEffect, useMemo, useState } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster } from "sonner";
import {
  LayoutDashboard,
  Map as MapIcon,
  Table2,
  Lightbulb,
  Quote,
  FileSearch,
} from "lucide-react";

import { fetchStats, fetchWorkshops } from "./lib/api";
import { heartbeat, setUnauthorizedHandler } from "./lib/auth";

import StatusBanner from "./components/StatusBanner";
import Header from "./components/Header";
import OverviewTab from "./components/OverviewTab";
import MapTab from "./components/MapTab";
import WorkshopsTab from "./components/WorkshopsTab";
import ThemesTab from "./components/ThemesTab";
import EvidenceTab from "./components/EvidenceTab";
import WorkshopDrawer from "./components/WorkshopDrawer";
import AuthGate from "./components/AuthGate";
import AdminPage from "./pages/AdminPage";

const TABS = [
  { id: "overview", label: "Overview", icon: LayoutDashboard },
  { id: "map", label: "Map view", icon: MapIcon },
  { id: "workshops", label: "Workshop explorer", icon: Table2 },
  { id: "themes", label: "Themes & patterns", icon: Lightbulb },
  { id: "evidence", label: "Evidence", icon: Quote },
];

function AppShell() {
  const [activeTab, setActiveTab] = useState("overview");
  const [workshops, setWorkshops] = useState([]);
  const [stats, setStats] = useState(null);
  const [totalTarget, setTotalTarget] = useState(22);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [authTick, setAuthTick] = useState(0); // force re-render on 401

  // Force the AuthGate to show again on any 401.
  useEffect(() => {
    setUnauthorizedHandler(() => setAuthTick((t) => t + 1));
  }, []);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [w, s] = await Promise.all([fetchWorkshops(), fetchStats()]);
      setWorkshops(w.workshops || []);
      setTotalTarget(w.total_target || 22);
      setStats(s);
    } catch (e) {
      console.error("Failed to load data", e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
    const interval = setInterval(refresh, 15000);
    const heart = setInterval(heartbeat, 30000);
    return () => {
      clearInterval(interval);
      clearInterval(heart);
    };
  }, [refresh]);

  const selectedWorkshop = useMemo(
    () => workshops.find((w) => w.id === selectedId) || null,
    [workshops, selectedId]
  );

  const loadedCount = workshops.length;

  return (
    <AuthGate key={authTick}>
      <div className="App min-h-screen bg-[#FAF9F6] text-gray-900">
        <StatusBanner loaded={loadedCount} target={totalTarget} />
        <Header />

        {/* Tab navigation */}
        <nav
          className="bg-white border-b border-gray-200 sticky top-0 z-40"
          data-testid="main-nav"
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex space-x-6 overflow-x-auto no-scrollbar">
              {TABS.map(({ id, label, icon: Icon }) => {
                const active = activeTab === id;
                return (
                  <button
                    key={id}
                    data-testid={`tab-${id}`}
                    onClick={() => setActiveTab(id)}
                    className={
                      "flex items-center gap-2 py-4 text-sm whitespace-nowrap transition-colors border-b-2 -mb-px " +
                      (active
                        ? "border-slate-800 text-slate-900 font-medium"
                        : "border-transparent text-gray-500 hover:text-gray-800")
                    }
                  >
                    <Icon className="h-4 w-4" />
                    {label}
                  </button>
                );
              })}
            </div>
          </div>
        </nav>

        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {loading && workshops.length === 0 ? (
            <LoadingState />
          ) : workshops.length === 0 ? (
            <EmptyState />
          ) : (
            <>
              {activeTab === "overview" && (
                <OverviewTab
                  stats={stats}
                  workshops={workshops}
                  onSelect={setSelectedId}
                />
              )}
              {activeTab === "map" && (
                <MapTab workshops={workshops} onSelect={setSelectedId} />
              )}
              {activeTab === "workshops" && (
                <WorkshopsTab workshops={workshops} onSelect={setSelectedId} />
              )}
              {activeTab === "themes" && (
                <ThemesTab stats={stats} workshops={workshops} />
              )}
              {activeTab === "evidence" && (
                <EvidenceTab workshops={workshops} onSelect={setSelectedId} />
              )}
            </>
          )}
        </main>

        <WorkshopDrawer
          workshop={selectedWorkshop}
          open={!!selectedWorkshop}
          onClose={() => setSelectedId(null)}
        />

        <footer className="border-t border-gray-200 bg-white py-6 mt-12">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-xs text-gray-500 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div>
              Community Engagement Insights · South Downs Region
            </div>
            <a
              href="#"
              className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
              data-testid="footer-credit"
              aria-label="Built by Community Power Solutions"
            >
              <span className="font-mono uppercase tracking-wider text-[10px] text-gray-400">
                Built by
              </span>
              <img
                src="https://customer-assets.emergentagent.com/job_workshops-wind-data/artifacts/lvwu5z7c_CPS_Trans_400.png"
                alt="Community Power Solutions"
                className="h-20 w-auto object-contain"
              />
            </a>
          </div>
        </footer>

        <Toaster position="bottom-right" />
      </div>
    </AuthGate>
  );
}

function LoadingState() {
  return (
    <div
      className="flex flex-col items-center justify-center py-24 text-gray-500"
      data-testid="loading-state"
    >
      <FileSearch className="h-8 w-8 mb-3 animate-pulse" />
      <p className="text-sm">Loading workshop data…</p>
    </div>
  );
}

function EmptyState() {
  return (
    <div
      className="border border-dashed border-gray-300 bg-white p-12 text-center"
      data-testid="empty-state"
    >
      <FileSearch className="h-10 w-10 mx-auto text-gray-400 mb-4" />
      <h2 className="text-xl font-semibold text-gray-900 mb-2">
        Awaiting workshop data
      </h2>
      <p className="text-sm text-gray-600 max-w-lg mx-auto">
        Workshop reports will appear here as they are ingested. The dashboard
        updates automatically every 15 seconds while batches are being
        processed.
      </p>
    </div>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/admin" element={<AdminPage />} />
        <Route path="*" element={<AppShell />} />
      </Routes>
    </BrowserRouter>
  );
}
